/**
 * Transport module (doc 10.3). One tiny fetch wrapper the whole client shares.
 *
 * - The opaque `cwos_session` cookie flows automatically (`credentials:'include'`);
 *   the client never holds or sends an IdP token (doc 10.3 "Do not add token handling").
 * - Paths are the backend's exact route paths (the Vite dev proxy / nginx forward
 *   them verbatim), so callers pass e.g. `/api/alerts` unchanged.
 * - 401 → {@link UnauthorizedError}; 403 → {@link ForbiddenError}; 404 →
 *   {@link NotFoundError}; other non-2xx → {@link ApiError}. Never returns a
 *   non-OK body as data.
 * - EVERY request carries `x-request-id`. See {@link REQUEST_ID_HEADER} below.
 */
import { ApiError, ForbiddenError, NotFoundError, UnauthorizedError, } from './errors.js';
/**
 * Correlation header. The API already honours an inbound `x-request-id`
 * (apps/api/src/server.ts: `genReqId` reads it, `requestIdHeader` names it, an
 * onRequest hook echoes it, and the logging allowlist permits `requestId`), so
 * an id generated here becomes the id of the server's own request record and
 * appears on every log line the request produces.
 *
 * Nothing generated one. The header appeared nowhere in this package or in the
 * admin app, so the correlation the server had carefully built was never
 * reachable from the client: a person saying "it failed when I pressed Save"
 * gave an operator no way to find the request in Cloud Logging. Every call now
 * sends one, and the value is attached to any error thrown so a screen can show
 * it and a support ticket can quote it.
 *
 * A caller-supplied `x-request-id` is honoured rather than overwritten — that is
 * how the client-error report ties itself to the id it already showed the user
 * (see clientErrors.ts).
 */
export const REQUEST_ID_HEADER = 'x-request-id';
let idCounter = 0;
/**
 * A fresh request id.
 *
 * `crypto.randomUUID` where it exists (every browser this app supports, and
 * Node). The fallback is for React Native: Hermes has no WebCrypto, and the
 * worker app shares this transport. Collision resistance there does not need to
 * be cryptographic — the value is a log-correlation key, not a secret or a
 * capability — so time + counter + two random words is sufficient, and a
 * missing id would be worse than a weak one.
 */
export function newRequestId() {
    const webcrypto = globalThis.crypto;
    if (typeof webcrypto?.randomUUID === 'function') {
        try {
            return webcrypto.randomUUID();
        }
        catch {
            /* fall through to the portable form */
        }
    }
    idCounter += 1;
    const rand = () => Math.random().toString(16).slice(2, 10);
    return `c-${Date.now().toString(36)}-${idCounter.toString(36)}-${rand()}${rand()}`;
}
/**
 * Ensure `headers` carries a request id, and return it.
 *
 * Header names are compared case-insensitively: `fetch` treats them that way,
 * so a caller passing `X-Request-Id` must not end up sending two.
 */
function ensureRequestId(headers) {
    for (const key of Object.keys(headers)) {
        if (key.toLowerCase() !== REQUEST_ID_HEADER)
            continue;
        const supplied = headers[key];
        if (supplied)
            return supplied;
        delete headers[key];
    }
    const id = newRequestId();
    headers[REQUEST_ID_HEADER] = id;
    return id;
}
/**
 * The id to report for a completed call: the one the server echoed if it echoed
 * one, otherwise the one we sent. They are normally identical — the difference
 * only shows up when something between here and the API rewrote it, which is
 * itself worth seeing.
 */
function echoedRequestId(res, sent) {
    try {
        return res.headers.get(REQUEST_ID_HEADER) || sent;
    }
    catch {
        return sent;
    }
}
/**
 * A `fetch` that rejected never reached the API, so there is no response to read
 * the id from — but the id was still sent and may well appear in a proxy log.
 * Attach it to the thrown value so the failure is not anonymous, without
 * changing what type callers catch (screens branch on `instanceof ApiError`, and
 * turning a network failure into one would be a behaviour change).
 */
function attachRequestId(err, requestId) {
    if (typeof err === 'object' && err !== null && !('requestId' in err)) {
        try {
            Object.defineProperty(err, 'requestId', {
                value: requestId,
                enumerable: false,
                configurable: true,
            });
        }
        catch {
            /* frozen or exotic value — the id is not worth a second failure */
        }
    }
    return err;
}
let baseUrl = '';
let authToken = null;
/**
 * Point the client at an absolute API origin. Defaults to same-origin ('') so
 * the dev proxy / nginx reverse-proxy handles routing. Called once at startup
 * when a cross-origin API is configured.
 */
export function configureBaseUrl(url) {
    baseUrl = url.replace(/\/+$/, '');
}
/**
 * Opaque bearer session token for non-browser clients. React Native has no
 * cookie jar, so the worker app holds the BFF session token in the keychain and
 * sends it as `Authorization: Bearer <token>`. When set, every request carries
 * that header; the browser admin SPA leaves it unset and continues to rely on
 * the `cwos_session` cookie (`credentials: 'include'`).
 */
export function configureAuthToken(token) {
    authToken = token;
}
function toQueryString(params) {
    if (!params)
        return '';
    const usp = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null)
            continue;
        usp.append(key, String(value));
    }
    const s = usp.toString();
    return s ? `?${s}` : '';
}
function messageFrom(body, fallback) {
    if (body && typeof body === 'object') {
        const m = body.message;
        if (typeof m === 'string' && m.length > 0)
            return m;
    }
    return fallback;
}
function permissionFrom(body) {
    if (body && typeof body === 'object') {
        const p = body.permission;
        if (typeof p === 'string' && p.length > 0)
            return p;
    }
    return null;
}
/**
 * The structured refusals of a privilege-ceiling 403, or null when the body did
 * not carry them.
 *
 * `PrivilegeCeilingError`'s 403 body — the shape EVERY authority-granting door
 * in the product refuses with — has no top-level `permission`. It carries
 * `refusals: [{ code, permissionKey, scope, message }]`, and reading only
 * `body.permission` discarded all of it: the closed reason code, the key at
 * fault, and the scope. What reached a screen was the joined `message` alone,
 * which cannot be branched on.
 *
 * Parsed defensively and shape-checked per entry, because this runs on a body
 * from the network: an entry that is not the expected shape is DROPPED rather
 * than coerced, and a `refusals` key that is not a non-empty array of valid
 * entries yields `null` — "the body did not say" — rather than `[]`, which
 * would claim a refusal with no reasons that the server cannot produce.
 */
function refusalsFrom(body) {
    if (!body || typeof body !== 'object')
        return null;
    const raw = body.refusals;
    if (!Array.isArray(raw))
        return null;
    const parsed = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object')
            continue;
        const e = entry;
        if (typeof e.code !== 'string' || typeof e.message !== 'string')
            continue;
        const key = e.permissionKey;
        const scope = e.scope;
        parsed.push({
            code: e.code,
            message: e.message,
            permissionKey: typeof key === 'string' ? key : null,
            scope: scope && typeof scope === 'object'
                ? {
                    scopeType: String(scope.scopeType ?? ''),
                    scopeId: typeof scope.scopeId === 'string'
                        ? scope.scopeId
                        : null,
                }
                : null,
        });
    }
    return parsed.length > 0 ? Object.freeze(parsed) : null;
}
/**
 * The eligibility guard's machine-readable flag, when the body carried it.
 *
 * `apps/api/src/domains/eligibility/guard.ts` sends `restricted: true` on the
 * 403 it raises for a worker under a hold, expressly so a client can render the
 * restricted state "rather than string-matching the sentence". Reading it here
 * is what makes that intention real on this side of the wire; before this, the
 * field arrived on every such refusal and nothing in the product looked at it.
 *
 * Deliberately not widened to any truthy value. The flag asserts a restriction
 * applies, and a `'false'` string or a `1` from some future proxy is not that
 * assertion — an over-eager read here would put a held worker's screen in front
 * of somebody who is not held.
 */
function restrictedFrom(body) {
    if (!body || typeof body !== 'object')
        return false;
    return body.restricted === true;
}
/** Marks a body that came back as something other than JSON. */
const NOT_JSON = Symbol('not-json');
function notJson(body) {
    return typeof body === 'object' && body !== null && NOT_JSON in body;
}
async function parseBody(res) {
    const text = await res.text();
    if (!text)
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        // NOT returned as data. A 200 whose body will not parse is a ROUTING
        // failure, not a payload: the classic case is a same-origin proxy that has
        // no rule for this family, so the SPA's own index.html comes back with a
        // 200. Handing that string to a screen is how `data.restricted.count`
        // became "Cannot read properties of undefined" on the mileage screen, and
        // how the documentation screen came to report zero records instead of
        // failing. `send` turns this into an error the UI can show.
        return { [NOT_JSON]: text };
    }
}
/**
 * Turn a status + body into the typed error a screen branches on, or return
 * for any 2xx.
 *
 * EXTRACTED SO THAT TWO TRANSPORTS CANNOT DISAGREE (F221b). It was inline in
 * `send`, which was fine while `send` was the only way to reach the API. It no
 * longer is: {@link postEventStream} receives a failed turn's status and body
 * inside a frame rather than as an HTTP response, and it must raise the SAME
 * error — a streaming turn refused for a spent confirmation has to reach the
 * surface as the same `ApiError(409)` the blocking one does, or the screen that
 * branches on it renders the wrong state for a reason no test would catch. One
 * function, both callers, no drift.
 */
export function raiseForStatus(status, body, requestId) {
    if (status === 401) {
        throw new UnauthorizedError(messageFrom(body, 'Your session has ended.'), body, requestId);
    }
    if (status === 403) {
        throw new ForbiddenError(messageFrom(body, 'This area needs a permission you do not hold.'), body, permissionFrom(body), requestId, refusalsFrom(body), 
        // `restricted: true` — the eligibility guard's refusal, which is about the
        // PERSON and not about a key. Lifted to a field for the same reason
        // `permission` is: a screen must be able to branch on it without reaching
        // into an untyped body, and this is the one 403 whose remedy is a
        // supervisor rather than an administrator. Strictly `=== true`: a missing
        // key and an absent body both mean "an ordinary 403", never "unknown".
        restrictedFrom(body));
    }
    if (status === 404) {
        // A DISTINCT TYPE BECAUSE SOME 404s ARE ANSWERS, NOT ACCIDENTS. Domains
        // that conceal existence answer 404 on purpose (see errors.ts), and a
        // screen must be able to branch on that without reading `status` off an
        // untyped `ApiError`. `NotFoundError extends ApiError`, so any caller
        // already testing `instanceof ApiError && status === 404` keeps working.
        throw new NotFoundError(messageFrom(body, 'That is not available.'), body, requestId);
    }
    if (status < 200 || status >= 300) {
        throw new ApiError(status, messageFrom(body, `Request failed (${status}).`), body, requestId);
    }
}
async function send(path, init) {
    const url = `${baseUrl}${path}${toQueryString(init.query)}`;
    const headers = { ...(init.headers ?? {}) };
    if (authToken)
        headers['authorization'] = `Bearer ${authToken}`;
    const sentRequestId = ensureRequestId(headers);
    let payload;
    if (init.body !== undefined) {
        headers['content-type'] = 'application/json';
        payload = JSON.stringify(init.body);
    }
    let res;
    try {
        res = await fetch(url, {
            method: init.method,
            credentials: 'include',
            headers,
            body: payload,
            signal: init.signal,
        });
    }
    catch (err) {
        throw attachRequestId(err, sentRequestId);
    }
    const requestId = echoedRequestId(res, sentRequestId);
    const parsed = await parseBody(res);
    // An unparseable body carries no fields worth attaching to an error, and may
    // be a whole HTML document — so errors below see null rather than the text.
    const body = notJson(parsed) ? null : parsed;
    raiseForStatus(res.status, body, requestId);
    if (notJson(parsed)) {
        // Deliberately not a generic "something went wrong": this failure has one
        // cause and one fix, and saying so is what stops it recurring.
        throw new ApiError(res.status, `The server answered ${path} with something other than data. This usually means the ` +
            `path is not routed to the API.`, null, requestId);
    }
    return body;
}
export function get(path, options) {
    return send(path, {
        method: 'GET',
        query: options?.query,
        signal: options?.signal,
        headers: options?.headers,
    });
}
/**
 * Parse the filename out of a Content-Disposition header (RFC 5987 `filename*`
 * preferred, plain `filename` as fallback). Never returns a path: any slash or
 * backslash in the value is rejected wholesale, so a caller can safely hand
 * the result to a download anchor.
 */
function parseContentDispositionFilename(value) {
    if (!value)
        return null;
    const star = /filename\*=UTF-8''([^;]+)/i.exec(value);
    if (star?.[1]) {
        try {
            const decoded = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
            if (!/[\\/]/.test(decoded))
                return decoded;
        }
        catch {
            /* fall through to plain form */
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(value);
    if (plain?.[1]) {
        const name = plain[1].trim();
        if (!/[\\/]/.test(name))
            return name;
    }
    return null;
}
/**
 * GET that returns the raw text body instead of JSON — for CSV/reporting
 * exports. Error semantics match `get` exactly (401/403/other → typed errors).
 */
/**
 * A row-count header, or `null` for every way of not having one — absent,
 * blank, or something that is not a finite number. Never 0, which is a real
 * measured answer and has to be distinguishable from "the server did not say".
 */
function parseRowCount(raw) {
    if (raw === null || raw.trim() === '')
        return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}
export async function download(path, options) {
    const url = `${baseUrl}${path}${toQueryString(options?.query)}`;
    const headers = { ...(options?.headers ?? {}) };
    if (authToken)
        headers['authorization'] = `Bearer ${authToken}`;
    const sentRequestId = ensureRequestId(headers);
    let res;
    try {
        res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers,
            signal: options?.signal,
        });
    }
    catch (err) {
        throw attachRequestId(err, sentRequestId);
    }
    const requestId = echoedRequestId(res, sentRequestId);
    const text = await res.text();
    let body = null;
    if (!res.ok) {
        try {
            body = text ? JSON.parse(text) : null;
        }
        catch {
            body = text;
        }
    }
    if (res.status === 401) {
        throw new UnauthorizedError(messageFrom(body, 'Your session has ended.'), body, requestId);
    }
    if (res.status === 403) {
        throw new ForbiddenError(messageFrom(body, 'This area needs a permission you do not hold.'), body, permissionFrom(body), requestId);
    }
    if (res.status === 404) {
        // A DISTINCT TYPE BECAUSE SOME 404s ARE ANSWERS, NOT ACCIDENTS. Domains
        // that conceal existence answer 404 on purpose (see errors.ts), and a
        // screen must be able to branch on that without reading `status` off an
        // untyped `ApiError`. `NotFoundError extends ApiError`, so any caller
        // already testing `instanceof ApiError && status === 404` keeps working.
        throw new NotFoundError(messageFrom(body, 'That is not available.'), body, requestId);
    }
    if (!res.ok) {
        throw new ApiError(res.status, messageFrom(body, `Request failed (${res.status}).`), body, requestId);
    }
    return {
        text,
        contentType: res.headers.get('content-type'),
        filename: parseContentDispositionFilename(res.headers.get('content-disposition')),
        truncated: res.headers.get('x-export-truncated') === 'true',
        rowCount: parseRowCount(res.headers.get('x-export-row-count')),
    };
}
export function post(path, body, options) {
    return send(path, {
        method: 'POST',
        body,
        query: options?.query,
        signal: options?.signal,
        headers: options?.headers,
    });
}
/**
 * POST expecting an SSE response: report each non-terminal frame, resolve with
 * the terminal one.
 *
 * THE PROMISE STILL RESOLVES ONCE, WHEN THE WORK IS DONE. That is the property
 * that matters most about this function and the reason it is shaped as a
 * promise with a callback rather than as an async iterator. Its caller —
 * `intelligence.turn` — keeps the exact signature it had, so a surface's
 * pending state begins and ends at precisely the moments it did before
 * streaming existed. The admin conversation surface guards double submission
 * with a synchronous in-flight ref cleared in the mutation's `onSettled`; if
 * "pending" ended early, that guard would open mid-turn and a second
 * activation would run a second turn. Progress is a SIDE CHANNEL, deliberately,
 * so that it cannot move that boundary.
 *
 * `onFrame` is called for every frame before the terminal one. It is wrapped so
 * that a throwing callback cannot reject the turn: a render bug in a progress
 * line must not destroy an answer the server has already computed, audited and
 * stored.
 *
 * FALLBACK. A server (or an intermediary) that answers with plain JSON instead
 * of an event stream is honoured rather than failed — the body is parsed as the
 * whole response and returned, with no frames reported. That keeps a caller
 * working against a deployment that has not got this route yet.
 */
export async function postEventStream(path, body, onFrame, options) {
    const url = `${baseUrl}${path}${toQueryString(options?.query)}`;
    const headers = { ...(options?.headers ?? {}) };
    if (authToken)
        headers['authorization'] = `Bearer ${authToken}`;
    headers['content-type'] = 'application/json';
    headers['accept'] = 'text/event-stream';
    const sentRequestId = ensureRequestId(headers);
    let res;
    try {
        res = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(body),
            signal: options?.signal,
        });
    }
    catch (err) {
        throw attachRequestId(err, sentRequestId);
    }
    const requestId = echoedRequestId(res, sentRequestId);
    // A refusal decided BEFORE the turn ran is a real HTTP status — the route
    // opens its stream only after every such check has passed — so it is read
    // here exactly as `send` would read it.
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('text/event-stream')) {
        const parsed = await parseBody(res);
        const parsedBody = notJson(parsed) ? null : parsed;
        raiseForStatus(res.status, parsedBody, requestId);
        return parsedBody;
    }
    const reader = res.body?.getReader();
    if (!reader)
        throw new ApiError(res.status, 'The server sent no response body.', null, requestId);
    const decoder = new TextDecoder();
    let buffer = '';
    let terminal;
    const consume = (chunk) => {
        buffer += chunk;
        // Frames are separated by a blank line. Anything after the last separator
        // is a partial frame and stays in the buffer until the rest arrives —
        // reading it early is how a chunked parser invents truncated events.
        let split = buffer.indexOf('\n\n');
        while (split !== -1) {
            const raw = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            split = buffer.indexOf('\n\n');
            let event = 'message';
            const dataLines = [];
            for (const line of raw.split('\n')) {
                if (line.startsWith(':'))
                    continue; // comment / keepalive
                if (line.startsWith('event:'))
                    event = line.slice(6).trim();
                else if (line.startsWith('data:'))
                    dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0)
                continue;
            let data;
            try {
                data = JSON.parse(dataLines.join('\n'));
            }
            catch {
                continue; // a frame that is not JSON is not this protocol's
            }
            if (event === 'result' || event === 'failure') {
                terminal = { event, data };
                continue;
            }
            try {
                onFrame(data);
            }
            catch {
                // See the docblock: a progress-rendering failure must never cost the
                // answer. Deliberately swallowed, never rethrown.
            }
        }
    };
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            consume(decoder.decode(value, { stream: true }));
        }
        consume(decoder.decode());
    }
    catch (err) {
        throw attachRequestId(err, sentRequestId);
    }
    if (terminal === undefined) {
        // The socket closed with no terminal frame. Never silently treated as an
        // empty answer: that is the class of failure where a screen reports zero
        // records because a request died, and it must be loud.
        throw new ApiError(res.status, 'The turn ended without a result. It may or may not have completed — reopen the ' +
            'conversation to see whether it was saved before trying again.', null, requestId);
    }
    if (terminal.event === 'failure') {
        const framed = terminal.data;
        const status = typeof framed.status === 'number' ? framed.status : 500;
        raiseForStatus(status, framed.body ?? null, requestId);
        // `raiseForStatus` returns without throwing only for a 2xx, which the
        // server never puts in a failure frame. Reaching here means the frame
        // was malformed, and saying so beats returning a body that is not one.
        throw new ApiError(status, 'The server reported a failure it could not describe.', null, requestId);
    }
    return terminal.data;
}
export function patch(path, body, options) {
    return send(path, {
        method: 'PATCH',
        body,
        query: options?.query,
        signal: options?.signal,
        headers: options?.headers,
    });
}
export function put(path, body, options) {
    return send(path, {
        method: 'PUT',
        body,
        query: options?.query,
        signal: options?.signal,
        headers: options?.headers,
    });
}
/**
 * `body` is optional and usually absent. It exists for BULK removals, where the
 * thing being removed is a set rather than one addressable resource (e.g.
 * closing several people's program assignments at once). A DELETE with a body is
 * legal HTTP; inventing a `/remove` verb endpoint to avoid it would be worse.
 */
/**
 * PUT with a RAW (non-JSON) body — image uploads. `send` above hardcodes
 * JSON.stringify on any body; this variant hands `fetch` the bytes as-is with
 * the caller's content type, and shares the same credentials/request-id/error
 * conventions by reusing the pieces `send` is built from.
 */
export async function putBinary(path, body, contentType, options) {
    const url = `${baseUrl}${path}${toQueryString(options?.query)}`;
    const headers = { ...(options?.headers ?? {}) };
    if (authToken)
        headers['authorization'] = `Bearer ${authToken}`;
    headers['content-type'] = contentType;
    const sentRequestId = ensureRequestId(headers);
    let res;
    try {
        res = await fetch(url, {
            method: 'PUT',
            credentials: 'include',
            headers,
            body: body,
            signal: options?.signal,
        });
    }
    catch (err) {
        throw attachRequestId(err, sentRequestId);
    }
    const requestId = echoedRequestId(res, sentRequestId);
    const parsed = await parseBody(res);
    const responseBody = notJson(parsed) ? null : parsed;
    if (res.status === 401) {
        throw new UnauthorizedError(messageFrom(responseBody, 'Your session has ended.'), responseBody, requestId);
    }
    if (res.status === 403) {
        throw new ForbiddenError(messageFrom(responseBody, 'This area needs a permission you do not hold.'), responseBody, permissionFrom(responseBody), requestId);
    }
    if (res.status === 404) {
        throw new NotFoundError(messageFrom(responseBody, 'That is not available.'), responseBody, requestId);
    }
    if (!res.ok) {
        throw new ApiError(res.status, messageFrom(responseBody, `Request failed (${res.status}).`), responseBody, requestId);
    }
    return responseBody;
}
export function del(path, options) {
    return send(path, {
        method: 'DELETE',
        body: options?.body,
        query: options?.query,
        signal: options?.signal,
        headers: options?.headers,
    });
}
//# sourceMappingURL=transport.js.map